import { useState, useEffect, useRef, useCallback } from 'react'
import { subscribeToTopics } from '../firebase/topics'
import {
  fitTextInBox,
  drawTextBlock,
  drawBackground,
  getTextSafeArea
} from '../utils/adImageCanvas'
import './AdImageCreator.css'

// Default background options: id, name, url, width, height (aspect used for canvas)
const DEFAULT_BACKGROUNDS = [
  { id: 'social-1080', name: 'Social 1080×1080', url: '', width: 1080, height: 1080, placeholder: true },
  { id: 'social-1200', name: 'Social 1200×628', url: '', width: 1200, height: 628, placeholder: true },
  { id: 'story-1080', name: 'Story 1080×1920', url: '', width: 1080, height: 1920, placeholder: true },
  { id: 'landscape-1200', name: 'Landscape 1200×800', url: '', width: 1200, height: 800, placeholder: true }
]

const FONT_OPTIONS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Impact', label: 'Impact' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'system-ui', label: 'System UI' }
]

const PRESET_COLORS = [
  '#ffffff', '#000000', '#1e40af', '#dc2626', '#059669', '#d97706', '#7c3aed', '#0e7490'
]

const AdImageCreator = () => {
  const [backgrounds, setBackgrounds] = useState(() =>
    DEFAULT_BACKGROUNDS.map(b => ({ ...b, url: b.url || createPlaceholderDataUrl(b.width, b.height) }))
  )
  const [selectedBg, setSelectedBg] = useState(backgrounds[0]?.id || null)
  const [topics, setTopics] = useState([])
  const [selectedTopicId, setSelectedTopicId] = useState(null)
  const [fontFamily, setFontFamily] = useState('Arial')
  const [fontColor, setFontColor] = useState('#ffffff')
  const [customBgUrl, setCustomBgUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [batchGenerating, setBatchGenerating] = useState(false)
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 })
  const canvasRef = useRef(null)
  const bgImageRef = useRef(null)

  const selectedBackground = backgrounds.find(b => b.id === selectedBg)
  const selectedTopic = topics.find(t => t.id === selectedTopicId)
  const displayText = selectedTopic?.text || ''

  // Load topics from Firebase
  useEffect(() => {
    const unsub = subscribeToTopics((list) => {
      setTopics(list || [])
      setLoading(false)
      if (!selectedTopicId && list?.length) setSelectedTopicId(list[0].id)
    })
    return () => unsub()
  }, [])

  // Resolve background image (custom URL or selected)
  const effectiveBgUrl = customBgUrl.trim() || selectedBackground?.url
  const effectiveWidth = selectedBackground?.width || 1080
  const effectiveHeight = selectedBackground?.height || 1080

  // Load background image into ref
  useEffect(() => {
    if (!effectiveBgUrl) {
      bgImageRef.current = null
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      bgImageRef.current = img
      redraw()
    }
    img.onerror = () => {
      bgImageRef.current = null
      redraw()
    }
    img.src = effectiveBgUrl
    return () => {
      img.src = ''
      bgImageRef.current = null
    }
  }, [effectiveBgUrl])

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const w = effectiveWidth
    const h = effectiveHeight
    if (canvas.width !== w) canvas.width = w
    if (canvas.height !== h) canvas.height = h

    const img = bgImageRef.current
    if (img && img.complete) {
      drawBackground(ctx, img, w, h)
    } else {
      ctx.fillStyle = '#1e293b'
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = 'rgba(255,255,255,0.1)'
      ctx.font = '24px system-ui'
      ctx.textAlign = 'center'
      ctx.fillText('No background image', w / 2, h / 2)
    }

    if (displayText.trim()) {
      const safe = getTextSafeArea(w, h, 12)
      const { fontSize, lines } = fitTextInBox(ctx, displayText, safe.width, safe.height, fontFamily, 14, 140)
      drawTextBlock(ctx, lines, safe.x, safe.y, fontSize, fontFamily, fontColor, {
        shadowBlur: 6,
        strokeWidth: 2,
        strokeColor: 'rgba(0,0,0,0.7)'
      })
    }
  }, [effectiveWidth, effectiveHeight, displayText, fontFamily, fontColor])

  useEffect(() => {
    redraw()
  }, [redraw])

  function createPlaceholderDataUrl(width, height) {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    const gradient = ctx.createLinearGradient(0, 0, width, height)
    gradient.addColorStop(0, '#334155')
    gradient.addColorStop(1, '#1e293b')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
    return canvas.toDataURL('image/png')
  }

  const exportImage = useCallback((filename) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const link = document.createElement('a')
    link.download = filename || `ad-image-${Date.now()}.png`
    link.href = canvas.toDataURL('image/png')
    link.click()
  }, [])

  const handleSaveImage = () => {
    const name = selectedTopic?.text?.slice(0, 30).replace(/[^a-z0-9]/gi, '-') || 'ad'
    const bgName = selectedBackground?.name?.replace(/\s+/g, '-') || 'bg'
    exportImage(`${bgName}-${name}-${Date.now()}.png`)
  }

  const runBatch = useCallback(async (mode) => {
    if (!selectedBackground && mode === 'all-topics') return
    const topicList = topics.filter(t => t?.text)
    if (!topicList.length && mode === 'all-topics') return
    if (!displayText.trim() && mode === 'all-backgrounds') return
    setBatchGenerating(true)
    const total = mode === 'all-topics' ? topicList.length : backgrounds.length
    setBatchProgress({ current: 0, total })
    for (let i = 0; i < total; i++) {
      setBatchProgress({ current: i + 1, total })
      if (mode === 'all-topics') {
        setSelectedTopicId(topicList[i].id)
      } else {
        setSelectedBg(backgrounds[i].id)
      }
      await new Promise(r => setTimeout(r, 350))
      const topic = mode === 'all-topics' ? topicList[i] : selectedTopic
      const bg = mode === 'all-topics' ? selectedBackground : backgrounds[i]
      const name = topic?.text?.slice(0, 25).replace(/[^a-z0-9]/gi, '-') || 'ad'
      const bgName = bg?.name?.replace(/\s+/g, '-') || 'bg'
      exportImage(`${bgName}-${name}-${i + 1}.png`)
    }
    setBatchGenerating(false)
    setBatchProgress({ current: 0, total: 0 })
  }, [topics, backgrounds, selectedBackground, selectedTopic, displayText])

  return (
    <div className="ad-image-creator">
      <header className="ad-image-header">
        <h1>Ad Image Creator</h1>
        <p className="ad-image-subtitle">Choose a background, pick a topic, customize text style, and save your marketing image.</p>
      </header>

      <div className="ad-image-layout">
        <aside className="ad-image-controls">
          <section className="ad-image-section">
            <h3>Background</h3>
            <div className="ad-image-bg-grid">
              {backgrounds.map((bg) => (
                <button
                  key={bg.id}
                  type="button"
                  className={`ad-image-bg-thumb ${selectedBg === bg.id ? 'selected' : ''}`}
                  onClick={() => { setSelectedBg(bg.id); setCustomBgUrl('') }}
                  style={{ aspectRatio: `${bg.width}/${bg.height}` }}
                >
                  {bg.url ? (
                    <img src={bg.url} alt={bg.name} />
                  ) : (
                    <span>{bg.name}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="ad-image-custom-url">
              <label>Custom background URL (overrides preset)</label>
              <input
                type="url"
                placeholder="https://... or leave blank to use preset"
                value={customBgUrl}
                onChange={(e) => setCustomBgUrl(e.target.value)}
              />
            </div>
            {selectedBackground && (
              <div className="ad-image-custom-url">
                <label>Or set image URL for this preset</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={selectedBackground.url?.startsWith('http') ? selectedBackground.url : ''}
                  onChange={(e) => {
                    const url = e.target.value.trim()
                    setBackgrounds(prev =>
                      prev.map(b => (b.id === selectedBg ? { ...b, url: url || createPlaceholderDataUrl(b.width, b.height) } : b))
                    )
                  }}
                />
              </div>
            )}
          </section>

          <section className="ad-image-section">
            <h3>Topic</h3>
            {loading ? (
              <p className="ad-image-muted">Loading topics…</p>
            ) : (
              <select
                className="ad-image-select"
                value={selectedTopicId || ''}
                onChange={(e) => setSelectedTopicId(e.target.value || null)}
              >
                <option value="">— Select topic —</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.text?.slice(0, 60)}{t.text?.length > 60 ? '…' : ''}
                  </option>
                ))}
              </select>
            )}
          </section>

          <section className="ad-image-section">
            <h3>Text style</h3>
            <div className="ad-image-field">
              <label>Font</label>
              <select
                className="ad-image-select"
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
              >
                {FONT_OPTIONS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="ad-image-field">
              <label>Color</label>
              <div className="ad-image-color-row">
                <input
                  type="color"
                  value={fontColor}
                  onChange={(e) => setFontColor(e.target.value)}
                  className="ad-image-color-input"
                />
                <input
                  type="text"
                  value={fontColor}
                  onChange={(e) => setFontColor(e.target.value)}
                  className="ad-image-color-text"
                />
              </div>
              <div className="ad-image-color-presets">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`ad-image-color-swatch ${fontColor.toLowerCase() === c.toLowerCase() ? 'active' : ''}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setFontColor(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="ad-image-actions">
            <button
              type="button"
              className="ad-image-btn primary"
              onClick={handleSaveImage}
              disabled={!displayText.trim()}
            >
              Save image
            </button>
            <button
              type="button"
              className="ad-image-btn secondary"
              onClick={() => runBatch('all-topics')}
              disabled={batchGenerating || !topics.length}
            >
              {batchGenerating && batchProgress.total ? `Generating ${batchProgress.current}/${batchProgress.total}…` : 'Batch: this background + all topics'}
            </button>
            <button
              type="button"
              className="ad-image-btn secondary"
              onClick={() => runBatch('all-backgrounds')}
              disabled={batchGenerating || !displayText.trim()}
            >
              {batchGenerating && batchProgress.total ? `Generating ${batchProgress.current}/${batchProgress.total}…` : 'Batch: this topic + all backgrounds'}
            </button>
          </section>
        </aside>

        <main className="ad-image-preview">
          <div
            className="ad-image-canvas-wrap"
            style={{
              maxWidth: Math.min(600, effectiveWidth),
              aspectRatio: `${effectiveWidth}/${effectiveHeight}`
            }}
          >
            <canvas
              ref={canvasRef}
              className="ad-image-canvas"
              width={effectiveWidth}
              height={effectiveHeight}
            />
          </div>
          <p className="ad-image-dimensions">{effectiveWidth} × {effectiveHeight}</p>
        </main>
      </div>
    </div>
  )
}

export default AdImageCreator
